import NiceModal from "@ebay/nice-modal-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import ReminderEditor from "./ReminderEditor";

function intervalToSeconds(intervalValue) {
  if (typeof intervalValue === "number") {
    return intervalValue;
  }
  if (!intervalValue || typeof intervalValue !== "string") {
    return 0;
  }

  const isoMatch = intervalValue.match(/^P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?/);
  if (isoMatch) {
    const days = Number(isoMatch[1] || 0);
    const hours = Number(isoMatch[2] || 0);
    const minutes = Number(isoMatch[3] || 0);
    return days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60;
  }

  const pgDayTimeMatch = intervalValue.match(
    /^(?:(\d+)\s+days?\s+)?(\d{1,2}):(\d{2}):(\d{2})$/,
  );
  if (pgDayTimeMatch) {
    const days = Number(pgDayTimeMatch[1] || 0);
    const hours = Number(pgDayTimeMatch[2] || 0);
    const minutes = Number(pgDayTimeMatch[3] || 0);
    const seconds = Number(pgDayTimeMatch[4] || 0);
    return days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60 + seconds;
  }

  return 0;
}

function formatLeadtime(intervalValue) {
  const totalSeconds = intervalToSeconds(intervalValue);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days} Tag${days === 1 ? "" : "e"}`);
  if (hours) parts.push(`${hours} Std.`);
  if (minutes || parts.length === 0) parts.push(`${minutes} Min.`);
  return parts.join(" ");
}

export default function RemindersList({ project }) {
  const queryClient = useQueryClient();

  const remindersQuery = useQuery({
    queryKey: ["reminders", project?.id],
    queryFn: async () => {
      const response = await fetch(`/api/admin/reminder/${project.id}`, {
        credentials: "same-origin",
      });
      if (response.ok) {
        return response.json();
      }
      throw new Error("Network response was not ok");
    },
    placeholderData: [],
    dependsOn: project,
  });

  async function handleReminderDelete(reminder) {
    const response = await fetch(`/api/admin/reminder/${project.id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id: reminder.id }),
    });
    if (response.ok) {
      queryClient.invalidateQueries({ queryKey: ["reminders", project?.id] });
    } else {
      alert("Es ist ein Fehler beim Löschen des Reminders aufgetreten.");
    }
  }

  function handleEditReminderBtn(reminder = null) {
    NiceModal.show(ReminderEditor, {
      project: project,
      reminder: reminder,
      addReminderToState: () => {
        queryClient.invalidateQueries({ queryKey: ["reminders", project?.id] });
      },
    });
  }

  return (
    <div className="space-y-12">
      <div className="border-b border-gray-900/10 pb-12">
        <h2
          className="text-base font-semibold leading-7 text-gray-900"
          id="reminders"
        >
          Erinnerungen
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-600">
          Hier können Erinnerungen angelegt werden, die automatisch vor einem
          gebuchten Termin per E-Mail an die Kunden verschickt werden. Die
          Vorlaufzeit gibt an, wie lange vor dem Termin die Erinnerung
          verschickt wird. Es können mehrere Erinnerungen mit unterschiedlichen
          Vorlaufzeiten angelegt werden.
        </p>

        <ul className="divide-y divide-gray-100">
          {remindersQuery.data.map((reminder) => (
            <li
              key={reminder.id || String(reminder.leadtime)}
              className="flex items-center justify-between gap-x-6 py-5"
            >
              <div className="min-w-0">
                <div className="flex items-start gap-x-3">
                  <p className="text-sm font-semibold leading-6 text-gray-900">
                    {formatLeadtime(reminder.leadtime)}
                  </p>
                </div>
                <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500">
                  <p className="whitespace-nowrap">
                    {reminder.email_subject || ""}
                  </p>
                </div>
              </div>
              <div className="flex flex-none items-center gap-x-4">
                <button
                  onClick={() => handleEditReminderBtn(reminder)}
                  className="hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
                >
                  Bearbeiten
                </button>
                <button
                  onClick={() => handleReminderDelete(reminder)}
                  className="hidden rounded-md bg-red-800 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-gray-300 hover: hover:bg-stone-700 sm:block"
                >
                  Löschen
                </button>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-6 flex items-center justify-end gap-x-6">
          <button
            onClick={() => handleEditReminderBtn(null)}
            type="button"
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            + Reminder anlegen
          </button>
        </div>
      </div>
    </div>
  );
}
